import { Img, Text, Heading } from "../../components";
import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { motion } from "framer-motion";
import { useSpring, animated } from "@react-spring/web";

const accordionData = [
  {
    img: "images/services-1.svg",
    uiuxText: "UI UX Design",
    content: [
      {
        heading: "User Research",
        text: "Dive deep into user needs and preferences to craft intuitive experiences.",
      },
      {
        heading: "Prototyping and Wireframing",
        text: "Visualise ideas with interactive prototypes and wireframes.",
      },
      {
        heading: "Visual Design",
        text: "Create stunning interfaces that blend aesthetics with functionality.",
      },
    ],
  },
  {
    img: "images/services-2.svg",
    uiuxText: "Mobile APPLICATION Development",
    content: [
      {
        heading: "iOS & Android",
        text: "Build high-performance apps for iOS and Android platforms.",
      },
      {
        heading: "Cross-Platform Apps",
        text: "Leverage frameworks like React Native and Flutter for cross-platform apps.",
      },
      {
        heading: "App UI/UX",
        text: "Design intuitive and seamless mobile experiences for users.",
      },
    ],
  },
  {
    img: "images/services-3.svg",
    uiuxText: "Website Development",
    content: [
      {
        heading: "Responsive Design",
        text: "Develop responsive websites that adapt to all devices and screen sizes.",
      },
      {
        heading: "Frontend Development",
        text: "Craft interactive and dynamic front-end experiences using modern frameworks.",
      },
      {
        heading: "Backend Integration",
        text: "Build scalable and secure backend systems for seamless functionality.",
      },
    ],
  },
  {
    img: "images/services-4.svg",
    uiuxText: "Custom Software Development",
    content: [
      {
        heading: "Custom Solutions",
        text: "Tailor-made software for your specific business needs.",
      },
      {
        heading: "Scalable Architectures",
        text: "Design solutions that scale with your growth.",
      },
      {
        heading: "Integration Services",
        text: "Seamless integration with third-party tools and APIs.",
      },
    ],
  },
  {
    img: "images/services-5.svg",
    uiuxText: "Enterprise Software Solutions",
    content: [
      {
        heading: "ERP Solutions",
        text: "Streamline operations with enterprise resource planning systems.",
      },
      {
        heading: "CRM Development",
        text: "Manage customer relationships efficiently with CRM solutions.",
      },
      {
        heading: "Cloud Integration",
        text: "Migrate and scale enterprise applications with cloud integration.",
      },
    ],
  },
  {
    img: "images/services-6.svg",
    uiuxText: "DevOps and CI/CD",
    content: [
      {
        heading: "Continuous Integration",
        text: "Automate testing and integration workflows to ensure stability.",
      },
      {
        heading: "Continuous Deployment",
        text: "Seamlessly deploy applications with CI/CD pipelines.",
      },
      {
        heading: "Infrastructure as Code",
        text: "Manage infrastructure efficiently using IaC tools.",
      },
    ],
  },
  {
    img: "images/services-7.svg",
    uiuxText: "DIGITAL MARKETING",
    content: [
      {
        heading: "SEO & SEM",
        text: "Optimize websites and run targeted ad campaigns to improve visibility.",
      },
      {
        heading: "Content Marketing",
        text: "Create engaging and valuable content to drive traffic and leads.",
      },
      {
        heading: "Social Media Management",
        text: "Grow your online presence across social media platforms.",
      },
    ],
  },
];

export default function ServicesAndSolutionsSection() {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const contentRefs = useRef([]);

  const handleAccordionToggle = (index) => {
    setExpandedIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  return (
    <div className="mt-20 md:mt-0 sm:mt-0">
      <div className="relative rounded-[80px] sm:rounded-[50px] bg-black-900 py-20 lg:py-12 md:py-12 sm:py-9">
        <div className="relative flex-1">
          <div className="container-xs flex flex-col gap-5 xl:gap-4 lg:gap-5 lg:px-[150px] xl:px-[160px] md:px-[100px] sm:px-[40px] md:gap-5 sm:gap-5">
            <Heading
              size="text8xl"
              as="h2"
              className="w-[100%] text-[90px] font-medium uppercase text-white-a700 mb-2 mr-[-200px] lg:w-full xl:text-[80px] lg:text-[50px] md:w-full md:text-[48px] sm:text-center sm:text-[40px]"
            >
              Services and <br /> Solutions
            </Heading>

            {accordionData.map((item, index) => {
              const isOpen = expandedIndex === index;

              // eslint-disable-next-line react-hooks/rules-of-hooks
              const slideAnimation = useSpring({
                opacity: isOpen ? 1 : 0,
                height: isOpen ? contentRefs.current[index]?.scrollHeight || "auto" : 0,
                overflow: "hidden",
                config: { tension: 210, friction: 20 },
              });

              return (
                <div
                  key={`accordion-item-${index}`}
                  className="border-b-[1px] pt-2 lg:pt-2 md:pt-2 sm:pt-2 hover:border-gray-400"
                  style={{ borderColor: "#282828" }}
                >
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => handleAccordionToggle(index)}
                  >
                    <div className="flex items-center gap-8 lg:gap-6 md:gap-4">
                      <Img
                        src={item.img}
                        alt={`${item.uiuxText} Icon`}
                        className="h-10 w-10 lg:h-8 lg:w-8 md:h-6 md:w-6 sm:h-5 sm:w-5 object-contain"
                      />
                      <Heading
                        size="text3xl"
                        as="p"
                        className="text-[32px] font-light uppercase tracking-wide text-white-a700 lg:text-[28px] md:text-[25px] sm:text-[18px]"
                      >
                        {item.uiuxText}
                      </Heading>
                    </div>

                    <div className="relative flex items-center justify-center w-6 h-6">
                      <span
                        className={`absolute w-full h-[2px] transition-all duration-500 ${
                          isOpen ? "opacity-0" : "opacity-100"
                        }`}
                        style={{ backgroundColor: "#9D9FA1" }}
                      ></span>
                      <span
                        className={`absolute h-full w-[2px] bg-white-a700 transition-all duration-500 ${
                          isOpen ? "rotate-90" : "rotate-0"
                        }`}
                        style={{ backgroundColor: "#9D9FA1" }}
                      ></span>
                    </div>
                  </div>

                  <animated.div
                    ref={(el) => (contentRefs.current[index] = el)}
                    style={slideAnimation}
                    className="ml-[70px] py-[12px] xl:ml-[60px] lg:ml-[60px] md:ml-10 sm:ml-10 text-gray-400"
                  >
                    <ul className="list-disc pl-10 space-y-4 font-light text-[30px] xl:text-[22px] lg:text-[22px] md:text-sm sm:text-xs">
                      {item.content.map((contentItem, contentIndex) => (
                        <li key={`content-${contentIndex}`}>
                          {contentItem.heading}: {contentItem.text}
                        </li>
                      ))}
                    </ul>
                  </animated.div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
